import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { FieldDef, applyDerived } from '@/lib/inventario/fieldSchemas';

interface Props {
  fields: FieldDef[];
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  tipoEquipo?: string;
}

/**
 * Renderiza dinámicamente los datos técnicos específicos del tipo de equipo.
 * Siempre visible: si el tipo no tiene esquema, muestra un mensaje claro.
 */
export const DatosTecnicosFields: React.FC<Props> = ({ fields, values, onChange, tipoEquipo }) => {
  const set = (key: string, v: any) => {
    const next = applyDerived(fields, { ...values, [key]: v });
    onChange(next);
  };

  return (
    <div className="space-y-3 rounded-lg border-2 border-primary/30 bg-primary/5 p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold text-foreground">
          Datos técnicos
          {tipoEquipo ? (
            <span className="text-muted-foreground font-normal"> · {tipoEquipo}</span>
          ) : null}
        </div>
        {fields.length > 0 && (
          <span className="text-xs text-primary font-medium">
            {fields.length} {fields.length === 1 ? 'campo' : 'campos'} específicos
          </span>
        )}
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          {tipoEquipo
            ? `No hay datos técnicos específicos definidos para "${tipoEquipo}". Solo se guardarán los campos comunes.`
            : 'Selecciona un tipo de equipo para ver sus datos técnicos específicos.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(() => {
            // Agrupa campos consecutivos con el mismo `group` en una sola tarjeta.
            const items: Array<{ kind: 'single'; field: FieldDef } | { kind: 'group'; id: string; label: string; fields: FieldDef[] }> = [];
            const groupIndex = new Map<string, number>();
            for (const f of fields) {
              if (f.group) {
                const existing = groupIndex.get(f.group);
                if (existing != null) {
                  (items[existing] as any).fields.push(f);
                } else {
                  groupIndex.set(f.group, items.length);
                  items.push({ kind: 'group', id: f.group, label: f.groupLabel || f.label, fields: [f] });
                }
              } else {
                items.push({ kind: 'single', field: f });
              }
            }

            const renderControl = (f: FieldDef, hideLabel = false) => {
              const id = `dt-${f.key}`;
              const value = values[f.key] ?? '';
              const isDerived = !!f.derived;

              if (f.type === 'boolean') {
                return (
                  <div key={f.key} className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2">
                    {!hideLabel && <Label htmlFor={id} className="text-sm">{f.label}</Label>}
                    <Switch id={id} checked={!!value} onCheckedChange={(checked) => set(f.key, checked)} />
                  </div>
                );
              }
              if (f.type === 'select') {
                return (
                  <div key={f.key} className="space-y-1.5">
                    {!hideLabel && <Label htmlFor={id} className="text-xs text-muted-foreground">{f.label}</Label>}
                    <Select value={value || undefined} onValueChange={(v) => set(f.key, v)}>
                      <SelectTrigger id={id}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent className="bg-popover border border-border z-50">
                        {(f.options ?? []).map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              if (f.type === 'textarea') {
                return (
                  <div key={f.key} className="space-y-1.5 md:col-span-2">
                    {!hideLabel && <Label htmlFor={id} className="text-sm">{f.label}</Label>}
                    <Textarea id={id} value={value} onChange={(e) => set(f.key, e.target.value)} rows={2} />
                  </div>
                );
              }
              const inputType = f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text';
              const placeholder = f.type === 'monthyear' ? 'MM/AAAA' : f.placeholder;
              return (
                <div key={f.key} className="space-y-1.5">
                  {!hideLabel && (
                    <Label htmlFor={id} className="text-xs text-muted-foreground">
                      {f.label}
                      {isDerived && <span className="ml-1 text-xs text-muted-foreground">(auto)</span>}
                    </Label>
                  )}
                  <Input
                    id={id}
                    type={inputType}
                    placeholder={placeholder}
                    value={value}
                    readOnly={isDerived}
                    className={isDerived ? 'bg-muted text-muted-foreground' : ''}
                    onChange={(e) => set(f.key, e.target.value)}
                  />
                  {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
                </div>
              );
            };

            return items.map((it) => {
              if (it.kind === 'single') return renderControl(it.field);
              return (
                <div
                  key={`g-${it.id}`}
                  className="rounded-md border-2 border-primary/40 bg-background p-2 space-y-1.5"
                >
                  <Label className="text-sm font-semibold">{it.label}</Label>
                  <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                    {it.fields.map((f) => (
                      <div key={f.key} className={f.type === 'select' ? 'w-[140px]' : ''}>
                        {renderControl(f, true)}
                      </div>
                    ))}
                  </div>
                  {it.fields.some(f => f.help) && (
                    <p className="text-xs text-muted-foreground">
                      {it.fields.find(f => f.help)?.help}
                    </p>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
};
